import { useEffect, useMemo, useRef, useState } from "react";
import { sensitive } from "../lib/sensitive";

import { PhoneInput } from "../components/PhoneInput";
import { t } from "../i18n";
import { Icons, iconFor } from "../lib/icons";
import { isComplete, nationalDigits, toE164 } from "../lib/phone";
import type { Datapoint, Locale, Option } from "../types";

interface Common {
  datapoint: Datapoint;
  locale: Locale;
  busy: boolean;
  error: string | null;
  onSubmit: (value: unknown) => void;
}

/** Klawiatura dobrana do pola — na telefonie to różnica między 3 a 10 sekundami. */
function inputMode(datapoint: Datapoint): React.HTMLAttributes<HTMLInputElement>["inputMode"] {
  if (datapoint.type === "number") return "numeric";
  // Klawiatura wynika z walidacji, nie z osobnego typu pola. PESEL i numer
  // konta to zwykły tekst z sumą kontrolną — typ był drugą nazwą na to samo,
  // a dwie nazwy na jedno pojęcie w końcu się rozjeżdżają.
  const rules = datapoint.validation;
  if (rules?.checksum || (rules?.mask ?? "").includes("#")) return "numeric";
  if (datapoint.key === "telefon") return "tel";
  if (datapoint.key === "email") return "email";
  return "text";
}

/**
 * Maska pola: `#` cyfra, `A` litera, pozostałe znaki dosłownie.
 * Wartość `iban` grupuje po cztery znaki — sztywna maska wykluczyłaby konto zagraniczne.
 *
 * Formatujemy przy każdej zmianie, więc wklejenie z schowka też się układa.
 * Poprzednia implementacja blokowała wklejanie na telefonie (patrz audyt) —
 * tutaj nic nie odrzucamy, tylko przepisujemy to, co przyszło.
 */
function applyMask(value: string, mask: string): string {
  if (mask === "iban") {
    const raw = value.toUpperCase().replace(/[^0-9A-Z]/g, "");
    return (raw.match(/.{1,4}/g) ?? []).join(" ");
  }

  const chars = value.replace(/[^0-9A-Za-z]/g, "");
  let out = "";
  let at = 0;
  for (const slot of mask) {
    if (at >= chars.length) break;
    if (slot === "#" || slot === "A") {
      const wanted = slot === "#" ? /\d/ : /[A-Za-z]/;
      // Znak nie pasujący do slotu pomijamy, zamiast przerywać całe pole.
      while (at < chars.length && !wanted.test(chars[at])) at += 1;
      if (at >= chars.length) break;
      out += chars[at];
      at += 1;
    } else {
      out += slot;
    }
  }
  return out;
}

/** Spacja jest kosmetyczna; myślnik w kodzie pocztowym należy już do wartości. */
function unmask(value: string): string {
  return value.replace(/ /g, "");
}

export function TextInput({ datapoint, locale, busy, error, onSubmit }: Common) {
  const [value, setValue] = useState("");
  const ref = useRef<HTMLInputElement>(null);
  const isPhone = datapoint.key === "telefon";
  const mask = datapoint.validation?.mask ?? null;

  useEffect(() => {
    setValue("");
    // Bez auto-focusu na dotyku: klawiatura zasłaniałaby treść pytania.
    if (window.matchMedia("(pointer: fine)").matches) ref.current?.focus();
  }, [datapoint.key]);

  const submit = () => {
    if (busy) return;
    if (isPhone) {
      onSubmit(toE164(nationalDigits(value)));
      return;
    }
    onSubmit(mask ? unmask(value.trim()) : value.trim());
  };

  if (isPhone) {
    return (
      <>
        <PhoneInput
          digits={nationalDigits(value)}
          onChange={setValue}
          onEnter={submit}
          label={datapoint.title}
        />
        {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
        {error && <p className="error">{error}</p>}
        <div className="actions">
          <button
            className={`btn btn--primary${busy ? " puls" : ""}`}
            onClick={submit}
            disabled={busy || !isComplete(nationalDigits(value))}
          >
            {busy ? t("saving", locale) : t("save", locale)}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <input
        ref={ref}
        className={`input${error ? " input--error" : ""}`}
        type={datapoint.type === "date" ? "date" : "text"}
        inputMode={inputMode(datapoint)}
        autoComplete={datapoint.key === "email" ? "email" : datapoint.key === "telefon" ? "tel" : "off"}
        placeholder={datapoint.placeholder ?? ""}
        value={value}
        aria-label={datapoint.title}
        aria-invalid={Boolean(error)}
        {...sensitive(datapoint)}
        onChange={(event) =>
          setValue(mask ? applyMask(event.target.value, mask) : event.target.value)
        }
        onKeyDown={(event) => event.key === "Enter" && submit()}
      />
      {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
      {error && <p className="error">{error}</p>}
      <div className="actions">
        {/* Pole nieobowiązkowe wolno zatwierdzić pusto — ten sam przycisk,
            tylko z inną etykietą, żeby nie dokładać elementu do ekranu. */}
        {/* Puls (samo krycie) sygnalizuje zapis w toku — etykieta i tak mówi
            „Zapisuję", ale ruch widać szybciej, niż czyta się słowo. */}
        <button
          className={`btn btn--primary${busy ? " puls" : ""}`}
          onClick={submit}
          disabled={busy || (!value.trim() && !datapoint.optional)}
        >
          {busy
            ? t("saving", locale)
            : datapoint.optional && !value.trim()
              ? t("skip", locale)
              : t("save", locale)}
        </button>
      </div>
    </>
  );
}

/** Karta wyboru — podstawowy element systemu (patrz DESIGN.md §5). */
export function ChoiceCard({
  fieldKey,
  option,
  selected,
  disabled,
  onSelect,
  index,
}: {
  fieldKey: string;
  option: Option;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  /** Pozycja w kaskadzie wejścia (--i dla .kaskada z tokens.css). */
  index?: number;
}) {
  const Icon = Icons[iconFor(fieldKey, option.id)];
  return (
    <button
      type="button"
      className="choice"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      style={index === undefined ? undefined : ({ "--i": index } as React.CSSProperties)}
      onClick={onSelect}
    >
      <span className="choice__icon">
        <Icon />
      </span>
      <span className="choice__body">
        <span className="choice__title">{option.label}</span>
      </span>
      <span className="choice__check" aria-hidden="true">
        <Icons.check size={20} />
      </span>
    </button>
  );
}

/**
 * Tekst do wyszukiwania bez polskich znaków i wielkości liter: „Wągrowiec",
 * „wagrowiec" i „WĄGROWIEC" to to samo. Część etykiet w słownikach (urzędy
 * skarbowe) jest zapisana bez ogonków, a kandydat wpisuje z ogonkami — i odwrotnie;
 * bez tego urząd był na liście, ale wyszukiwarka go nie znajdowała. „ł" nie
 * rozkłada się w Unicode na literę + znak, więc zamieniamy je osobno.
 */
export function fold(text: string): string {
  return foldChars(text).replace(/\s+/g, " ").trim();
}

/** To samo co `fold`, ale znak po znaku — długość zostaje, więc indeksy pasują do oryginału. */
function foldChars(text: string): string {
  return Array.from(text)
    .map((ch) => (ch === "ł" || ch === "Ł" ? "l" : ch.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() || ch))
    .join("");
}

export function SelectInput({ datapoint, locale, busy, error, onSubmit }: Common) {
  const options = useMemo<Option[]>(() => datapoint.options ?? [], [datapoint]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    setQuery("");
    setPicked(null);
  }, [datapoint.key]);

  const choose = (id: string) => {
    setPicked(id);
    onSubmit(id);
  };

  // Do sześciu opcji — karty do dotknięcia. Powyżej — lista z wyszukiwarką,
  // bo kart z kodami wiz czy setką urzędów nikt nie przeczyta.
  if (!datapoint.searchable) {
    return (
      <>
        {/* Karty wchodzą kaskadą (tokens.css) — montują się raz na pytanie,
            więc błąd walidacji niczego nie przemontowuje i nie powtarza wejścia. */}
        <div className="choices kaskada" role="radiogroup" aria-label={datapoint.title}>
          {options.map((option, index) => (
            <ChoiceCard
              key={option.id}
              fieldKey={datapoint.key}
              option={option}
              selected={picked === option.id}
              disabled={busy}
              onSelect={() => choose(option.id)}
              index={index}
            />
          ))}
        </div>
        {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
        {error && <p className="error">{error}</p>}
      </>
    );
  }

  const words = fold(query).split(" ").filter(Boolean);
  const matches = words.length
    ? options.filter((option) => {
        const label = fold(option.label);
        return words.every((word) => label.includes(word));
      })
    : options;

  return (
    <>
      <input
        className="input"
        type="text"
        role="combobox"
        aria-expanded="true"
        aria-label={datapoint.title}
        placeholder={t("search", locale)}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="list">
        {matches.length === 0 && <div className="list__empty">{t("noResults", locale)}</div>}
        {matches.slice(0, 80).map((option) => (
          <button
            key={option.id}
            type="button"
            className="list__item"
            disabled={busy}
            onClick={() => choose(option.id)}
          >
            {highlight(option.label, words[0] ?? "")}
          </button>
        ))}
      </div>
      {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
      {error && <p className="error">{error}</p>}
    </>
  );
}

function highlight(label: string, needle: string) {
  if (!needle) return label;
  // Szukamy po tekście bez ogonków, a podświetlamy w oryginale — „wagr" zaznacza „Wągr".
  const at = foldChars(label).indexOf(needle);
  if (at < 0 || foldChars(label).length !== label.length) return label;
  return (
    <>
      {label.slice(0, at)}
      <mark>{label.slice(at, at + needle.length)}</mark>
      {label.slice(at + needle.length)}
    </>
  );
}

export function CheckboxInput({ datapoint, locale, busy, error, onSubmit }: Common) {
  const [checked, setChecked] = useState(false);
  useEffect(() => setChecked(false), [datapoint.key]);

  return (
    <>
      <button
        type="button"
        className="choice"
        role="checkbox"
        aria-checked={checked}
        onClick={() => setChecked((previous) => !previous)}
      >
        <span className="choice__icon">
          <Icons.signature />
        </span>
        <span className="choice__body">
          <span className="choice__title">{datapoint.title}</span>
        </span>
        <span className="choice__check" aria-hidden="true">
          <Icons.check size={20} />
        </span>
      </button>
      {datapoint.hint && <p className="field__hint">{datapoint.hint}</p>}
      {error && <p className="error">{error}</p>}
      <div className="actions">
        <button
          className={`btn btn--primary${busy ? " puls" : ""}`}
          disabled={busy || !checked}
          onClick={() => onSubmit("tak")}
        >
          {busy ? t("saving", locale) : t("save", locale)}
        </button>
      </div>
    </>
  );
}
